
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Line } from 'vue-chartjs';
import {
  QuotaAndGroupUsage,
  UsageRepositoryInterface,
  WebsiteBandwidthUsage,
  WebsiteGroupBandwidthUsage,
} from '@/services/Usage/interfaces';
import BandwidthLineChart from "@/views/pages/usage/components/BandwidthLineChart.vue";
import { PlanInterface } from "@/services/Plan/interfaces";

const LineChart = Line;

interface BandwidthSiteUsage {
  websiteId: number;
  domain: string;
  isMain: boolean;
  limit: number;
  siteTraffic: number;
  ftpTraffic: number;
  emailTraffic: number;
  dailyUsage: {
    date: string;
    siteTraffic: number;
    ftpTraffic: number;
    emailTraffic: number;
  }[];
}

@Component({
  components: {
    BandwidthLineChart,
  },
  inject: ['usageRepository'],
})
export default class BandwidthTab extends Vue {
  @Prop({ required: true }) readonly plan!: PlanInterface | null;

  private readonly usageRepository!: UsageRepositoryInterface;

  private expandedWebsiteId: number | null = null;

  private loading = false;
  private error: string | null = null;
  private bandwidthUsage: WebsiteGroupBandwidthUsage | null = null;

  get groupId(): number {
    return Number(this.$route.params.groupId);
  }

  async mounted(): Promise<void> {
    await this.load();
  }

  private getBeginningOfMonthUnixSeconds(): number {
    const now = new Date();

    console.log(new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0))
    return Math.floor(
      new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0).getTime() / 1000,
    );
  }

  private async load(): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      const from = this.getBeginningOfMonthUnixSeconds();

      this.bandwidthUsage = await this.usageRepository.getBandwidthUsageByGroup(this.groupId, from);

      if (this.bandwidthUsage.websiteUsage.length) {
        this.expandedWebsiteId = this.bandwidthUsage.websiteUsage[0].websiteId;
      }
    } catch (e) {
      this.bandwidthUsage = null;
      this.error = e?.message || 'Failed to load bandwidth usage';
    } finally {
      this.loading = false;
    }
  }

  private getWebsiteTotal(site: WebsiteBandwidthUsage): number {
    return site.usage.reduce((sum, point) => sum + point.usage, 0);
  }

  get chartPoints(): Array<{ x: Date; y: number }> {
    if (!this.selectedSite) return [];

    return this.selectedSite.usage.map(point => ({
      x: new Date(point.timestamp * 1000),
      y: point.usage / 1024 / 1024,
    }));
  }

  get sites(): WebsiteBandwidthUsage[] {
    return this.bandwidthUsage?.websiteUsage || [];
  }

  get selectedSite(): WebsiteBandwidthUsage | null {
    return this.sites.find(s => s.websiteId === this.expandedWebsiteId) || null;
  }

  get totalUsed(): number {
    return this.bandwidthUsage?.totalUsage || 0;
  }

  get totalLimit(): number {
    return (this.plan?.bandwidth ?? 0) * 1024;
  }

  get progressPercent(): number {
    if (!this.totalLimit) return 0;
    return Math.min((this.totalUsed / this.totalLimit) * 100, 100);
  }

  get chartOptions(): any {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          labels: {
            color: 'rgba(255,255,255,0.65)',
          },
        },
      },
      scales: {
        x: {
          ticks: { color: 'rgba(255,255,255,0.55)' },
          grid: { display: false },
        },
        y: {
          beginAtZero: true,
          ticks: { color: 'rgba(255,255,255,0.55)' },
          grid: { color: 'rgba(255,255,255,0.06)' },
        },
      },
    };
  }

  private toggleExpanded(websiteId: number): void {
    this.expandedWebsiteId = this.expandedWebsiteId === websiteId ? null : websiteId;
  }

  private formatSize(value: number): string {
    if (!value || value <= 0) return '0 KB';

    const mb = value / 1024 / 1024;
    const gb = value / 1024 / 1024 / 1024;

    if (mb < 1) return `${(value / 1024).toFixed(2)} KB`;
    if (gb < 1) return `${mb.toFixed(2)} MB`;

    return `${gb.toFixed(2)} GB`;
  }
}
